import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Select, Textarea } from "@/components/base/Field";
import { createAccountRequest } from "@/services/requestService";
import PlanPicker from "@/components/feature/PlanPicker";
import { CYCLE_SCOPE_LABEL } from "@/utils/cycleScope";
import { PLAN_MAX_CYCLES } from "@/utils/plans";
import type { CycleCode, SchoolPlan } from "@/types";

interface RequestAccountModalProps {
  open: boolean;
  onClose: () => void;
  /** Forfait choisi sur la grille des tarifs (Établissement par défaut). */
  initialPlan?: SchoolPlan;
}

interface FormState {
  schoolName: string;
  shortName: string;
  code: string;
  city: string;
  country: string;
  cycles: CycleCode[];
  plan: SchoolPlan;
  contactName: string;
  contactRole: string;
  email: string;
  phone: string;
  studentsEstimate: string;
  message: string;
}

const emptyForm: FormState = {
  schoolName: "",
  shortName: "",
  code: "",
  city: "",
  country: "Guinée",
  cycles: ["PRIMAIRE", "SECONDAIRE"],
  plan: "ETABLISSEMENT",
  contactName: "",
  contactRole: "Directeur / Directrice",
  email: "",
  phone: "",
  studentsEstimate: "",
  message: "",
};

const cycleOptions: CycleCode[] = ["PRIMAIRE", "SECONDAIRE"];
const estimateOptions = ["Moins de 200", "200 - 500", "500 - 1000", "Plus de 1000"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Change le forfait ; Essentiel ne garde qu'un cycle. */
function withPlan(form: FormState, plan: SchoolPlan): FormState {
  const max = PLAN_MAX_CYCLES[plan];
  return { ...form, plan, cycles: max === null ? form.cycles : form.cycles.slice(0, max) };
}

export default function RequestAccountModal({ open, onClose, initialPlan = "ETABLISSEMENT" }: RequestAccountModalProps) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState("");
  const [honeypot, setHoneypot] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(withPlan(emptyForm, initialPlan));
    setErrors({});
    setFormError("");
    setSaving(false);
    setSubmitted(false);
    setReference("");
    setHoneypot("");
  }, [open, initialPlan]);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleCycle = (cycle: CycleCode) => {
    setForm((prev) => ({
      ...prev,
      cycles: prev.cycles.includes(cycle)
        ? prev.cycles.filter((c) => c !== cycle)
        : PLAN_MAX_CYCLES[prev.plan] === 1
          ? [cycle]
          : [...prev.cycles, cycle],
    }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.schoolName.trim()) next.schoolName = "Le nom de l'école est obligatoire.";
    if (!form.city.trim()) next.city = "La ville est obligatoire.";
    if (!form.contactName.trim()) next.contactName = "Le nom du responsable est obligatoire.";
    if (!form.email.trim()) next.email = "L'email est obligatoire.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Format d'email invalide.";
    if (form.cycles.length === 0) next.cycles = "Sélectionnez au moins un cycle.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Champ auxiliaire anti-robot : si rempli, on affiche la même confirmation
    // qu'une soumission normale sans rien enregistrer.
    if (honeypot.trim()) {
      setReference("");
      setSubmitted(true);
      return;
    }
    setFormError("");
    if (!validate()) {
      setFormError("Veuillez corriger les champs signalés avant d'envoyer votre demande.");
      return;
    }
    setSaving(true);
    try {
      const created = await createAccountRequest({
        schoolName: form.schoolName.trim(),
        shortName: form.shortName.trim() || form.schoolName.trim(),
        code: form.code.trim(),
        city: form.city.trim(),
        country: form.country.trim() || "Guinée",
        cycles: form.cycles,
        plan: form.plan,
        contactName: form.contactName.trim(),
        contactRole: form.contactRole.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        studentsEstimate: form.studentsEstimate,
        message: form.message.trim(),
        website: honeypot,
      });
      setReference(created.reference);
      setSubmitted(true);
    } catch (err) {
      setFormError(
        err instanceof Error && err.message
          ? err.message
          : "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={submitted ? "Demande transmise" : "Demander l'ouverture d'un compte"}
      subtitle={
        submitted
          ? "Votre demande est en attente de validation"
          : "Votre demande sera examinée par l'administrateur de la plateforme"
      }
      icon={submitted ? "ri-mail-check-line" : "ri-add-box-line"}
      size="lg"
      footer={
        submitted ? (
          <>
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
            <Link
              to="/suivre-demande"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-route-line"></i>
              Suivre ma demande
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-login-box-line"></i>
              Aller à la connexion
            </Link>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              form="account-request-form"
              icon="ri-send-plane-line"
              disabled={saving}
            >
              {saving ? "Envoi..." : "Envoyer ma demande"}
            </Button>
          </>
        )
      }
    >
      {submitted ? (
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-700">
            <i className="ri-checkbox-circle-fill text-3xl"></i>
          </span>
          <div>
            <p className="text-base font-semibold font-heading text-foreground-950">
              Merci, votre demande a bien été enregistrée.
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
              Elle sera examinée par l'administrateur de la plateforme. Une fois validée, votre école
              sera créée et vous pourrez vous connecter pour la configurer, puis accéder au tableau de
              bord.
            </p>
          </div>
          <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-100/60 px-4 py-3 text-left">
            <p className="text-xs uppercase tracking-wide text-foreground-400">
              Référence de la demande
            </p>
            <p className="mt-0.5 text-sm font-medium text-foreground-900">
              {reference || "—"}
            </p>
            <p className="mt-2 text-xs text-foreground-500">
              Email de contact : {form.email || "—"}
            </p>
          </div>
        </div>
      ) : (
        <form id="account-request-form" onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Champ auxiliaire anti-robot, masqué via la feuille de style */}
          <input
            type="text"
            name="website_alt"
            className="request-aux-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">
              Votre établissement
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Nom de l'école"
                name="schoolName"
                required
                placeholder="Ex : Groupe Scolaire Nongo Excellence"
                value={form.schoolName}
                onChange={(e) => set({ schoolName: e.target.value })}
                hint={errors.schoolName}
                wrapperClass="sm:col-span-2"
              />
              <Input
                label="Nom court"
                name="shortName"
                placeholder="Ex : Nongo Excellence"
                value={form.shortName}
                onChange={(e) => set({ shortName: e.target.value })}
                hint="Affiché dans les espaces compacts."
              />
              <Input
                label="Code / sigle"
                name="code"
                placeholder="Ex : GSE-NGO"
                value={form.code}
                onChange={(e) => set({ code: e.target.value })}
              />
              <Input
                label="Ville"
                name="city"
                required
                placeholder="Ex : Conakry"
                value={form.city}
                onChange={(e) => set({ city: e.target.value })}
                hint={errors.city}
              />
              <Input
                label="Pays"
                name="country"
                value={form.country}
                onChange={(e) => set({ country: e.target.value })}
              />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-foreground-700">Forfait souhaité</p>
            <PlanPicker value={form.plan} onChange={(plan) => setForm((prev) => withPlan(prev, plan))} />
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-foreground-700">
              Cycles souhaités <span className="text-accent-600">*</span>
              {PLAN_MAX_CYCLES[form.plan] === 1 && (
                <span className="font-normal text-foreground-500"> (un seul avec le forfait Essentiel)</span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {cycleOptions.map((cycle) => {
                const checked = form.cycles.includes(cycle);
                return (
                  <button
                    key={cycle}
                    type="button"
                    onClick={() => toggleCycle(cycle)}
                    className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors cursor-pointer ${
                      checked
                        ? "border-primary-300 bg-primary-50 text-primary-800"
                        : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    <i className={checked ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}></i>
                    {CYCLE_SCOPE_LABEL[cycle]}
                  </button>
                );
              })}
            </div>
            {errors.cycles && <p className="mt-1 text-xs text-accent-600">{errors.cycles}</p>}
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">
              Personne responsable
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Nom et prénom"
                name="contactName"
                required
                placeholder="Ex : Sékou Camara"
                value={form.contactName}
                onChange={(e) => set({ contactName: e.target.value })}
                hint={errors.contactName}
              />
              <Input
                label="Fonction"
                name="contactRole"
                value={form.contactRole}
                onChange={(e) => set({ contactRole: e.target.value })}
              />
              <Input
                label="Adresse email"
                name="email"
                type="email"
                required
                placeholder="vous@votre-ecole.edu.gn"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                hint={errors.email}
              />
              <Input
                label="Téléphone"
                name="phone"
                placeholder="+224 ..."
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
              />
              <Select
                label="Effectif approximatif"
                name="studentsEstimate"
                value={form.studentsEstimate}
                onChange={(e) => set({ studentsEstimate: e.target.value })}
                wrapperClass="sm:col-span-2"
              >
                <option value="">Non précisé</option>
                {estimateOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt} élèves
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Textarea
              label="Message (optionnel)"
              name="message"
              rows={3}
              maxLength={500}
              placeholder="Précisez vos besoins, le nombre de classes, votre échéance..."
              value={form.message}
              onChange={(e) => set({ message: e.target.value })}
              hint={`${form.message.length}/500 caractères`}
            />
          </div>

          {formError && (
            <div className="flex items-start gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-600">
                <i className="ri-error-warning-line text-base"></i>
              </span>
              <p className="text-xs text-foreground-700">{formError}</p>
            </div>
          )}

          <div className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-2.5">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-700">
              <i className="ri-information-line text-base"></i>
            </span>
            <p className="text-xs leading-relaxed text-foreground-600">
              L'envoi de ce formulaire crée une <strong className="font-medium text-foreground-900">demande</strong>,
              et non un compte. Seul l'administrateur de la plateforme peut valider l'ouverture.
            </p>
          </div>
        </form>
      )}
    </Modal>
  );
}